import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { AreaChart, Area, ResponsiveContainer, Tooltip, YAxis } from "recharts";
import { Fire, TrendUp, Minus, Mountains, Snowflake, Sparkle } from "@phosphor-icons/react";
import { Search } from "lucide-react";
import CopilotChat from "./CopilotChat";
import GuideHelpButton from "../components/GuideHelpButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PAGE_SIZE = 25;
const SELECT_CLS = "w-full sm:w-auto sm:min-w-[150px] sm:flex-1 px-3 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm font-medium text-zinc-700 focus:outline-none focus:border-violet-400";

const STATUS_META = {
  exploding: { label: "Exploding", color: "#10b981", chip: "bg-emerald-50 text-emerald-700 ring-emerald-200", Icon: Fire },
  rising: { label: "Rising", color: "#3b82f6", chip: "bg-blue-50 text-blue-700 ring-blue-200", Icon: TrendUp },
  regular: { label: "Steady", color: "#71717a", chip: "bg-zinc-100 text-zinc-600 ring-zinc-200", Icon: Minus },
  peaked: { label: "Peaked", color: "#f59e0b", chip: "bg-amber-50 text-amber-700 ring-amber-200", Icon: Mountains },
  cooling: { label: "Cooling", color: "#ef4444", chip: "bg-red-50 text-red-600 ring-red-200", Icon: Snowflake },
  collecting: { label: "Collecting…", color: "#a1a1aa", chip: "bg-zinc-100 text-zinc-500 ring-zinc-200", Icon: Sparkle },
};
const SORTS = [{ key: "opportunity", label: "Best opportunity" }, { key: "growth", label: "Fastest growing" }, { key: "volume", label: "Most searched" }];

const VERDICT_META = {
  emerging: { label: "Emerging", cls: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  competitive: { label: "Competitive", cls: "bg-amber-50 text-amber-700 ring-amber-200" },
  saturated: { label: "Saturated", cls: "bg-red-50 text-red-600 ring-red-200" },
  whitespace: { label: "💎 Whitespace", cls: "bg-violet-50 text-violet-700 ring-violet-200" },
  underserved: { label: "🟢 Underserved", cls: "bg-teal-50 text-teal-700 ring-teal-200" },
  unvalidated: { label: "Unvalidated", cls: "bg-zinc-100 text-zinc-500 ring-zinc-200" },
  brand: { label: "Brand", cls: "bg-zinc-100 text-zinc-400 ring-zinc-200" },
};
const verdictNote = (t) => {
  const n = t.n_funded || 0;
  if (t.verdict === "whitespace") return t.serp_evidence || "verified open lane — no product serving this yet";
  if (t.verdict === "underserved") return t.serp_evidence || "thin market — only 1–3 products rank · room to enter";
  if (t.verdict === "emerging") return n > 0 ? `${n} funded ${n === 1 ? "rival" : "rivals"} · room to enter` : "few players · room to enter";
  if (t.verdict === "competitive") return t.serp_checked ? (t.serp_evidence || `${n} rivals · need a sharp wedge`) : `${n} funded rivals · need a sharp wedge`;
  if (t.verdict === "saturated") return t.serp_checked ? (t.serp_evidence || `${n} funded rivals · crowded`) : `${n} funded rivals · crowded`;
  if (t.verdict === "unvalidated") return "real demand · market not yet verified";
  if (t.verdict === "brand") return "a specific product, not a market";
  return "";
};

const fmtGrowth = (g) => {
  if (g == null) return null;
  const a = Math.abs(g), s = g < 0 ? "−" : "+";
  if (a >= 9900) return `${s}99×+`;
  if (a >= 1000) return `${s}${Math.round(a / 100)}×`;
  return `${s}${Math.round(a)}%`;
};
const fmtVol = (v) => (v == null ? "—" : v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}K` : `${v}`);

const Sparkline = ({ data, color }) => {
  if (!data || data.length < 2) return <div className="h-12 flex items-center text-[10px] text-zinc-300">—</div>;
  const gid = `tpc-${color.replace("#", "")}`;
  return (
    <ResponsiveContainer width="100%" height={48}>
      <AreaChart data={data} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
        <defs><linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity={0.28} /><stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient></defs>
        <YAxis hide domain={[0, "dataMax"]} />
        <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #e4e4e7", padding: "3px 7px" }} formatter={(v) => [`${fmtVol(v)} searches`, "monthly"]} />
        <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#${gid})`} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

const Row = ({ t }) => {
  const meta = STATUS_META[t.status] || STATUS_META.regular;
  const growth = fmtGrowth(t.growth);
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("startups"); // startups | advisor
  const [related, setRelated] = useState(null);

  const expand = () => {
    const nx = !open;
    setOpen(nx);
    if (nx && related === null) {
      axios.get(`${API}/trending/topics-board/${t.slug}/startups?limit=10`)
        .then((r) => setRelated(r.data.startups || []))
        .catch(() => setRelated([]));
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-4 hover:border-violet-300 transition-colors" data-testid={`tpc-row-${t.slug}`}>
      <div className="flex items-start gap-4">
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-zinc-900 capitalize">{t.name}</div>
          {t.verdict && (
            <div className="mt-1" data-testid={`tpc-verdict-${t.slug}`}>
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ring-1 ${(VERDICT_META[t.verdict] || VERDICT_META.unvalidated).cls}`}>
                {(VERDICT_META[t.verdict] || VERDICT_META.unvalidated).label}
              </span>
              <span className="text-[10px] text-zinc-400 ml-1.5">{verdictNote(t)}</span>
            </div>
          )}
          <div className="text-xs text-zinc-500 mt-0.5">{t.description || "—"}</div>
          <div className="flex flex-wrap gap-1 mt-1.5">
            {(t.industries || []).slice(0, 3).map((c) => <span key={c} className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500">{c}</span>)}
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full ring-1 inline-flex items-center gap-1 ${meta.chip}`}><meta.Icon size={10} weight="fill" /> {meta.label}</span>
          </div>
        </div>
        <div className="hidden sm:block w-28 flex-shrink-0">
          <Sparkline data={t.series} color={meta.color} />
          <div className="text-center text-sm font-bold tabular-nums mt-0.5" style={{ color: meta.color }}>{growth || "—"}</div>
        </div>
        <div className="w-16 flex-shrink-0 text-right">
          <div className="text-sm font-bold text-zinc-900 tabular-nums">{fmtVol(t.avg_searches)}</div>
          <div className="text-[10px] text-zinc-400">searches/mo</div>
        </div>
      </div>

      <div className="mt-3 pt-3 border-t border-zinc-100">
        <button onClick={expand} data-testid={`tpc-expand-${t.slug}`}
          className="text-xs font-semibold text-violet-700 hover:text-violet-900 inline-flex items-center gap-1">
          <Sparkle size={13} weight="fill" /> {open ? "Hide" : "Who's building this + ask the Advisor"} {open ? "↑" : "→"}
        </button>
      </div>

      {open && (
        <div className="mt-3" data-testid={`tpc-detail-${t.slug}`}>
          <div className="inline-flex rounded-lg bg-zinc-100 p-0.5 mb-3">
            <button onClick={() => setTab("startups")} data-testid={`tpc-tab-startups-${t.slug}`}
              className={`px-3 py-1 rounded-md text-xs font-semibold ${tab === "startups" ? "bg-white text-zinc-900 shadow-sm" : "text-zinc-500"}`}>Who's building this</button>
            <button onClick={() => setTab("advisor")} data-testid={`tpc-tab-advisor-${t.slug}`}
              className={`px-3 py-1 rounded-md text-xs font-semibold ${tab === "advisor" ? "bg-white text-violet-700 shadow-sm" : "text-zinc-500"}`}>Ask the Advisor</button>
          </div>

          {tab === "startups" ? (
            related === null ? <div className="text-xs text-zinc-400 py-3">Finding who's building this…</div>
            : related.length === 0 ? <div className="text-xs text-zinc-500 py-3">No funded startups match this yet — validate real demand before building.</div>
            : (
              <div className="space-y-1.5">
                {related.map((s) => (
                  <div key={s.slug} className="flex items-center gap-2.5 p-2 rounded-lg bg-zinc-50 border border-zinc-100" data-testid={`tpc-startup-${s.slug}`}>
                    {s.logo_url
                      ? <img src={s.logo_url} alt="" className="w-7 h-7 rounded object-cover bg-white flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                      : <div className="w-7 h-7 rounded bg-violet-100 text-violet-600 flex items-center justify-center text-xs font-bold flex-shrink-0">{(s.name || "?")[0]}</div>}
                    <div className="min-w-0 flex-1">
                      <a href={s.website || "#"} target="_blank" rel="noopener noreferrer" className="text-sm font-semibold text-zinc-800 hover:text-violet-700 truncate block">{s.name}</a>
                      <div className="text-[11px] text-zinc-400 truncate">{s.description || (s.niches || []).join(" · ")}</div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-[11px] font-semibold text-zinc-600">{s.funding_total_usd ? `$${s.funding_total_usd >= 1e6 ? Math.round(s.funding_total_usd / 1e6) + "M" : Math.round(s.funding_total_usd / 1e3) + "K"}` : (s.funding_last_round || "—")}</div>
                      <div className="text-[10px] text-zinc-400">{fmtVol(s.avg_searches)}/mo</div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <CopilotChat context={{ type: "topic", slug: t.slug, label: t.name, section: t.section }} compact />
          )}
        </div>
      )}
    </div>
  );
};

export default function TopicsBoard({ onAskTrend }) {
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ total: 0, categories: [], sections: [] });
  const [sort, setSort] = useState("growth");
  const [category, setCategory] = useState("all");
  const [section, setSection] = useState("all");
  const [exploding, setExploding] = useState(false);
  const [emergingOnly, setEmergingOnly] = useState(false);
  const [whitespaceOnly, setWhitespaceOnly] = useState(false);
  const [underservedOnly, setUnderservedOnly] = useState(false);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => { axios.get(`${API}/trending/topics-board/facets`).then((r) => setFacets(r.data)).catch(() => {}); }, []);

  const fetchPage = useCallback((pageNum, replace) => {
    setLoading(true);
    axios.get(`${API}/trending/topics-board`, { params: { sort, category, section, status: exploding ? "exploding" : undefined, verdict: emergingOnly ? "emerging" : whitespaceOnly ? "whitespace" : underservedOnly ? "underserved" : undefined, q, page: pageNum, page_size: PAGE_SIZE } })
      .then((r) => { setTotal(r.data.total); setItems((prev) => (replace ? r.data.items : [...prev, ...r.data.items])); setPage(pageNum); })
      .finally(() => setLoading(false));
  }, [sort, category, section, exploding, emergingOnly, whitespaceOnly, underservedOnly, q]);

  useEffect(() => { const t = setTimeout(() => fetchPage(1, true), 300); return () => clearTimeout(t); }, [sort, category, section, exploding, emergingOnly, whitespaceOnly, underservedOnly, q, fetchPage]);

  const ask = (t) => { if (onAskTrend) onAskTrend(t); };

  return (
    <div data-testid="topics-board">
      <div className="text-xs font-bold tracking-widest text-violet-600 mb-2">TRENDING · TOPICS</div>
      <h1 className="text-3xl sm:text-4xl font-extrabold text-zinc-900 mb-2 flex items-center gap-2">
        Markets people are searching for.
        <GuideHelpButton feature="trending" />
      </h1>
      <p className="text-zinc-500 max-w-3xl text-sm">
        {facets.total?.toLocaleString()} markets people are actively searching for — each scored on <b>demand vs competition</b>. Tap <b className="text-emerald-700">💎 Emerging</b> for proven demand with few players and room to enter — that's where the real opportunities are. <b className="text-violet-700">💎 Whitespace</b> and <b className="text-teal-700">🟢 Underserved</b> are checked live against Google and are rare by design, so an empty tab means we looked and honestly found none.
      </p>

      {(facets.sections || []).length > 0 && (
        <div className="mt-5 space-y-2">
          {/* Verdict toggles — single scroll row on mobile, wraps on desktop */}
          <div className="filter-scroll flex flex-nowrap sm:flex-wrap gap-2 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="tpc-verdict-pills">
            <button onClick={() => setExploding((v) => !v)} data-testid="tpc-exploding-toggle"
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${exploding ? "bg-orange-500 text-white" : "bg-white border border-orange-200 text-orange-600 hover:border-orange-400"}`}>
              🔥 Exploding
            </button>
            <button onClick={() => { const nx = !emergingOnly; setEmergingOnly(nx); if (nx) { setWhitespaceOnly(false); setUnderservedOnly(false); setSort("opportunity"); } }} data-testid="tpc-emerging-toggle"
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${emergingOnly ? "bg-emerald-600 text-white" : "bg-white border border-emerald-200 text-emerald-700 hover:border-emerald-400"}`}>
              💎 Emerging {facets.verdicts?.emerging ? <span className={emergingOnly ? "text-emerald-200" : "text-emerald-400"}>{facets.verdicts.emerging.toLocaleString()}</span> : null}
            </button>
            <button onClick={() => { const nx = !whitespaceOnly; setWhitespaceOnly(nx); if (nx) { setEmergingOnly(false); setUnderservedOnly(false); setSort("opportunity"); } }} data-testid="tpc-whitespace-toggle"
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${whitespaceOnly ? "bg-violet-600 text-white" : "bg-white border border-violet-200 text-violet-700 hover:border-violet-400"}`}>
              💎 Whitespace {facets.verdicts?.whitespace ? <span className={whitespaceOnly ? "text-violet-200" : "text-violet-400"}>{facets.verdicts.whitespace.toLocaleString()}</span> : null}
            </button>
            <button onClick={() => { const nx = !underservedOnly; setUnderservedOnly(nx); if (nx) { setEmergingOnly(false); setWhitespaceOnly(false); setSort("opportunity"); } }} data-testid="tpc-underserved-toggle"
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${underservedOnly ? "bg-teal-600 text-white" : "bg-white border border-teal-200 text-teal-700 hover:border-teal-400"}`}>
              🟢 Underserved {facets.verdicts?.underserved ? <span className={underservedOnly ? "text-teal-200" : "text-teal-400"}>{facets.verdicts.underserved.toLocaleString()}</span> : null}
            </button>
          </div>
          {/* Section filters — single scroll row on mobile, wraps on desktop */}
          <div className="filter-scroll flex flex-nowrap sm:flex-wrap gap-2 -mx-4 px-4 sm:mx-0 sm:px-0" data-testid="tpc-section-pills">
            <button onClick={() => setSection("all")} data-testid="tpc-section-all"
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${section === "all" ? "bg-violet-600 text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-violet-300"}`}>
              All sections
            </button>
            {facets.sections.map((s) => (
              <button key={s.value} onClick={() => setSection(s.value)} data-testid={`tpc-section-${s.value.toLowerCase()}`}
                className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs sm:text-sm font-semibold transition-colors ${section === s.value ? "bg-violet-600 text-white" : "bg-white border border-zinc-200 text-zinc-600 hover:border-violet-300"}`}>
                {s.value} <span className={section === s.value ? "text-violet-200" : "text-zinc-400"}>{s.count.toLocaleString()}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 mb-4 p-3 sm:p-4 rounded-2xl border border-zinc-200 bg-zinc-50/70">
        <div className="relative mb-2.5">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a topic or market…"
            className="w-full pl-9 pr-4 py-2.5 rounded-xl border border-zinc-200 bg-white text-sm focus:outline-none focus:border-violet-400" data-testid="tpc-search" />
        </div>
        <div className="grid grid-cols-2 sm:flex sm:flex-wrap items-center gap-2">
          <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="tpc-cat-select" className={SELECT_CLS}>
            <option value="all">All categories</option>
            {(facets.categories || []).map((c) => <option key={c.value} value={c.value}>{c.value} ({c.count})</option>)}
          </select>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="tpc-sort" className={SELECT_CLS}>
            {SORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
      </div>

      <div className="text-sm text-zinc-500 mb-3">{total.toLocaleString()} topics</div>
      <div className="space-y-2">{items.map((t) => <Row key={t.slug} t={t} />)}</div>
      {loading && items.length === 0 && (
        <div className="space-y-2" data-testid="tpc-skeletons">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={`tsk-${i}`} className="bg-white border border-zinc-200 rounded-xl p-4 animate-pulse">
              <div className="flex items-start gap-4">
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="h-4 w-1/3 rounded bg-zinc-200" />
                  <div className="h-3 w-24 rounded bg-zinc-100" />
                  <div className="h-3 w-2/3 rounded bg-zinc-100" />
                </div>
                <div className="hidden sm:block w-28 h-12 rounded bg-zinc-100" />
                <div className="w-16 h-8 rounded bg-zinc-200" />
              </div>
            </div>
          ))}
        </div>
      )}
      {!loading && total === 0 && (whitespaceOnly || underservedOnly) && (
        <div className="mt-2 p-6 sm:p-8 rounded-2xl border border-zinc-200 bg-zinc-50/70 text-center" data-testid="tpc-empty-verified">
          <div className="text-3xl mb-2">🔍</div>
          <h3 className="text-base font-bold text-zinc-800">No verified {whitespaceOnly ? "open lanes" : "underserved markets"} right now</h3>
          <p className="text-sm text-zinc-500 max-w-xl mx-auto mt-2">
            We SERP-checked the live Google results for every strong candidate — across consumer AND B2B SaaS. Almost every searchable market already has products (and retailers) ranking, so genuine {whitespaceOnly ? "0-competitor whitespace" : "1–3 competitor"} gaps are rare by design. We refuse to fake a badge. <b>This is the honest signal — empty means we checked and found none.</b>
          </p>
          <button onClick={() => { setWhitespaceOnly(false); setUnderservedOnly(false); setEmergingOnly(true); setSort("opportunity"); }}
            data-testid="tpc-empty-to-emerging"
            className="mt-4 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-colors">
            See {facets.verdicts?.emerging ? facets.verdicts.emerging.toLocaleString() : ""} 💎 Emerging markets instead →
          </button>
          <p className="text-xs text-zinc-400 mt-2">Proven demand · few players · room to enter — the realistic place to start.</p>
        </div>
      )}
      {!loading && items.length < total && (
        <div className="flex justify-center mt-6">
          <button onClick={() => fetchPage(page + 1, false)} data-testid="tpc-load-more"
            className="px-6 py-2.5 rounded-full border border-zinc-200 text-sm font-semibold text-zinc-700 hover:border-violet-300 transition-colors">
            Load more · {(total - items.length).toLocaleString()} more
          </button>
        </div>
      )}
    </div>
  );
}
